import { reactive } from 'vue'

/**
 * Estado do shell (sidebar + header).
 *
 * SEMÂNTICA DE `open` — herdada, e não é o que o nome sugere. Leia antes de mexer.
 *
 * `open` não significa "sidebar visível". Ele liga a classe `.active` no `.sidebar-area`
 * (e `sidebar-hide` no `<body>`), e o CSS interpreta essa classe de dois jeitos, conforme
 * o viewport:
 *
 *   ≥1200px  →  `.active` = sidebar COLAPSADO (modo ícone, 60px). O sidebar está no fluxo
 *               e empurra o conteúdo; o toggle alterna expandido ↔ ícone.
 *   <1200px  →  `.active` = drawer ABERTO (sobrepondo o conteúdo). O sidebar sai do fluxo
 *               (`left: -100%`) e o toggle alterna oculto ↔ sobreposto.
 *
 * Ou seja: o MESMO `true` quer dizer "colapsado" no desktop e "aberto" no mobile. Era daí
 * que vinha o bug de o drawer aparecer sobreposto já no primeiro paint em telas menores —
 * o valor inicial fixo em `true` é o certo para desktop e o errado para mobile.
 *
 * O nome não foi trocado de propósito: `open` é lido por ~50 views (o `.action-point-2`) além
 * do próprio shell. Em vez de renomear, o estado derivado abaixo (`modoDrawer`,
 * `drawerAberto`, `sidebarExpandido`) dá os significados sem ambiguidade — prefira esses ao
 * ler `open` cru em código novo.
 */

// Mesmo limite do breakpoint `xl` do Bootstrap (ver scss/_bootstrap-variables.scss) e do
// `--sidebar-space: 0px` em design-system/tokens/spacing.css: é o ponto em que o sidebar deixa
// de reservar espaço no fluxo e passa a sobrepor.
const DRAWER_MEDIA_QUERY = '(max-width: 1199.98px)'

const consultarModoDrawer = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(DRAWER_MEDIA_QUERY).matches

const modoDrawerInicial = consultarModoDrawer()

const stateStore = reactive({
  /** Ver o bloco de documentação acima antes de usar. */
  open: !modoDrawerInicial,

  /** `true` quando o sidebar é drawer sobreposto (<1200px). Acompanha o resize. */
  modoDrawer: modoDrawerInicial,

  isFullScreen: false,

  /** Drawer sobreposto e visível — o caso que pede backdrop, trava de scroll e foco. */
  get drawerAberto(): boolean {
    return this.modoDrawer && this.open
  },

  /**
   * O sidebar está mostrando os rótulos? Serve para `aria-expanded` do botão de menu, que
   * precisa do significado real e não do valor cru de `open`.
   */
  get sidebarExpandido(): boolean {
    return this.modoDrawer ? this.open : !this.open
  },

  onChange() {
    stateStore.open = !stateStore.open
  },

  /** Fecha o drawer. Não faz nada no desktop, onde "fechado" não é um estado do drawer. */
  fecharDrawer() {
    if (stateStore.modoDrawer) {
      stateStore.open = false
    }
  },

  toggleFullScreen() {
    this.isFullScreen = !this.isFullScreen
  }
})

if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  const mq = window.matchMedia(DRAWER_MEDIA_QUERY)
  mq.addEventListener('change', (evento) => {
    stateStore.modoDrawer = evento.matches
    // Ao ENTRAR em modo drawer, força fechado: um `open: true` que no desktop significava
    // "colapsado" passaria a significar "drawer aberto", e o sidebar surgiria sobreposto sem
    // ninguém ter pedido. Saindo do modo drawer não há o que forçar — os dois valores de
    // `open` mapeiam para estados de desktop válidos (expandido ou ícone).
    if (evento.matches) {
      stateStore.open = false
    }
  })
}

export default stateStore
